/**
 * Allows the user to select an Entity from the given list of options. If you don't have a list
 * already, consider using EntityInputWithSearch, which performs API queries to search based on
 * the user's text input.
 */

import PropTypes from 'prop-types';
import React, {ReactNode} from 'react';
import _ from 'underscore';

import {ENTITY_SHAPE} from 'constants/UserConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Button, {ButtonProps, ButtonStyle} from 'ui-library/buttons/Button';
import LegacyTagsInput, {TagsInputDropdownOption} from 'ui-library/tags-input/LegacyTagsInput';
import {TagsInputSize, TagsInputSizePropType} from 'ui-library/tags-input/TagsInputConstants';
import * as Keyboard from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';

import {PartyInputQuery, PartyInputQueryVariables} from './__generated/PartyInputQuery';
import {EntitySuggestion, EntityWithText, MinimalEntity, PARTY_INPUT_QUERY} from './EntityInputConstants';
import {EntitySuggestionRow, getPartyDisplayName} from './EntitySuggestionRow';

export interface LegacyEntityInputProps {
  allOptions: Array<EntitySuggestion>;
  value: Array<MinimalEntity>;

  // Passed down to TagsInput
  allowAdjustInputWidth?: boolean;
  placeholder: string;
  addKeys?: Array<number>;

  // An optional button rendered inline next to the input. This should reflect the action the
  // user is taking on submit.
  buttonProps?: ButtonProps;
  allowUnvalidatedEntities?: boolean;
  // A function to filter the entities. Should return a list of entities that should be
  // rendered as options. You are given the user's text input as well as the full list of options
  filterOptions: (text: string, options: Array<EntityWithText>) => Array<EntityWithText>;

  // If no entities match, render emptyText in the dropdown.
  emptyText: ReactNode;

  // A function called when the user changes the input text, which is typically the entity's
  // handle or an email. The parent component can use this to monitor input changes, and possibly
  // modify the `allOptions` value in response.
  onChangeInput?: (text: string) => void;
  onChange: (value: Array<EntityWithText>) => void;
  onHoverOption?: (option: EntityWithText) => void;
  // A function called when the user hits "Enter" when it is not meaningful in the context of
  // this component. E.g. if the user has selected an option and has not entered additional text
  onEnter?: () => void;
  // Function called to handle special key strokes.
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  size: TagsInputSize;
  shouldAllowMultiple?: boolean;
  dataTestAttrs?: DataTestAttrs;

  disabled?: boolean;
}

const renderSuggestion = (size: TagsInputSize, suggestion: EntityWithText): JSX.Element => (
  <EntitySuggestionRow
    key={suggestion.handle}
    suggestion={suggestion}
    isSmallInput={size === TagsInputSize.SMALL}
  />
);

export function LegacyEntityInput(props: LegacyEntityInputProps): JSX.Element {
  const {buttonProps, emptyText, allOptions, value, ...passThroughProps} = props;
  const optionPartyIds = allOptions.map((opt) => opt.id);
  const valuePartyIds = value.map((v) => v.id);
  const allPartyIds = [...new Set([...optionPartyIds, ...valuePartyIds])].filter((id) => id != null);

  const handleKeyDown = (e: ElementKeyboardEvent): void => {
    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
    if (props.onEnter) {
      Keyboard.callOnEnter(props.onEnter)(e);
    }
  };

  // Re-used in BenchlingQuery.props.renderLoading because: if renderLoading doesn't render a TagsInput,
  // the user input stored in TagsInput.state will be dropped
  function renderTagsInput(tagsOptions: Array<EntitySuggestion>, tagsValue: Array<MinimalEntity>): ReactNode {
    return (
      <LegacyTagsInput
        allowAdding={false}
        allowSearching={true}
        allOptions={tagsOptions}
        value={tagsValue}
        button={buttonProps && <Button type="button" {...buttonProps} />}
        noMatchingOptions={<TagsInputDropdownOption>{emptyText}</TagsInputDropdownOption>}
        renderOption={renderSuggestion.bind(null, props.size)}
        onKeyDown={handleKeyDown}
        {...passThroughProps}
      />
    );
  }
  return (
    <BenchlingQuery<PartyInputQuery, PartyInputQueryVariables>
      query={PARTY_INPUT_QUERY}
      variables={{ids: allPartyIds}}
      renderLoading={() =>
        renderTagsInput(
          allOptions.map((opt) => ({...opt, text: getPartyDisplayName(opt)})),
          value.map((v) => ({...v, text: getPartyDisplayName(v)}))
        )
      }
    >
      {(data) => {
        const validParties = accessibleObjs(data.parties);
        const partyById = _.indexBy(validParties, 'id');
        const hydratedOptions = allOptions.map((opt) => ({
          ...opt,
          ...partyById[opt.id],
          text: getPartyDisplayName(partyById[opt.id] || opt),
        }));
        const hydratedValue = value.map((v) => ({
          ...v,
          ...partyById[v.id],
          text: getPartyDisplayName(partyById[v.id] || v),
        }));
        return renderTagsInput(hydratedOptions, hydratedValue);
      }}
    </BenchlingQuery>
  );
}

LegacyEntityInput.propTypes = {
  allOptions: PropTypes.arrayOf(
    PropTypes.shape({
      handle: PropTypes.string,
      name: PropTypes.string,
      id: PropTypes.string,
      text: PropTypes.string,
    })
  ).isRequired,
  value: PropTypes.arrayOf(ENTITY_SHAPE).isRequired,
  allowAdjustInputWidth: PropTypes.bool,
  placeholder: PropTypes.string.isRequired,
  addKeys: PropTypes.arrayOf(PropTypes.number),
  buttonProps: PropTypes.shape({
    buttonStyle: PropTypes.oneOf(Object.values(ButtonStyle)).isRequired,
    size: PropTypes.string.isRequired,
    onClick: PropTypes.func,
  }),
  allowUnvalidatedEntities: PropTypes.bool,
  filterOptions: PropTypes.func.isRequired,
  emptyText: PropTypes.string.isRequired,
  onChangeInput: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onHoverOption: PropTypes.func,

  shouldAllowMultiple: PropTypes.bool,
  size: TagsInputSizePropType.isRequired,
};

LegacyEntityInput.defaultProps = {
  allowUnvalidatedEntities: false,
  emptyText: 'No matches found.',
};
